/**
 * «HVEM HAR TILGANG» (D3, QA13 #107, 253.25) — `GET /v1/performances/:id/access` for eieren og
 * `GET /v1/advance-shares/:performanceId/access` for motparten i en delt advance.
 *
 * Lista regnes av den samme `can()` som avgjør tilgangen, kjørt for hver person, så den aldri kan
 * være uenig med reglene. Den har to deler, som delingsvinduet i Google Docs:
 *
 *  * én linje per ORGANISASJON med hvor mange som kan åpne programposten, som kan åpnes for navnene;
 *  * navngitte linjer for eksterne, inviterte, lenkepersoner og reisefølget med innlogging, med
 *    HVORDAN de fikk tilgang og HVA de kan gjøre.
 *
 * Begge parter ser navnene hos hverandre (§4.5, 328.1); før 4. november så byrået bare antallet.
 */
import { z } from 'zod';

import { ACTOR_ROLES } from '../enums.js';
import { wireInstant } from './wire-time.js';

export const ACCESS_ORGANIZATION_SIDES = ['owner', 'counterpart'] as const;

export const accessMemberSchema = z
  .object({
    personId: z.uuid(),
    displayName: z.string(),
    role: z.enum(ACTOR_ROLES),
  })
  .meta({ id: 'AccessMember' });
export type AccessMember = z.infer<typeof accessMemberSchema>;

export const accessOrganizationSchema = z
  .object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    /** Eieren av programposten, eller motparten i den delte advancen. */
    side: z.enum(ACCESS_ORGANIZATION_SIDES),
    /** Organisasjonen den innloggede er i. */
    mine: z.boolean(),
    /** Hvor mange som kan åpne programposten, regnet av `can()`. */
    count: z.number().int().nonnegative(),
    /** Navnene, når den innloggede får se dem; ellers null. */
    members: z.array(accessMemberSchema).nullable(),
  })
  .meta({ id: 'AccessOrganization' });
export type AccessOrganization = z.infer<typeof accessOrganizationSchema>;

/**
 * HVORDAN personen fikk tilgang. `external`: en ekstern med innlogging, `invited`: invitert og ikke
 * logget inn ennå, `link`: en personlig lenke uten innlogging, `touring_party`: står i reisefølget
 * og har innlogging (artistvisningen, 0380).
 */
export const ACCESS_HOWS = ['external', 'invited', 'link', 'touring_party'] as const;
export type AccessHow = (typeof ACCESS_HOWS)[number];

/** Hva delingen gjelder: programposten selv, hele årgangen eller avdelingen. */
export const ACCESS_VIAS = ['performance', 'edition', 'department', 'party'] as const;

export const accessPersonSchema = z
  .object({
    /** Delingens id, eller reisefølgeradens. Stabil nøkkel for lista. */
    key: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    how: z.enum(ACCESS_HOWS),
    via: z.enum(ACCESS_VIAS),
    /** Nivået på delingen; null for reisefølget, som ser det byrået har slått på. */
    permission: z.enum(['view', 'edit']).nullable(),
    /** Fanene delingen dekker; null er hele programposten (aldri kontrakt og økonomi). */
    scopes: z.array(z.string()).nullable(),
    expiresAt: wireInstant.nullable(),
    /** F17: organisasjonen som delte videre, når det ikke er eieren. */
    grantedByOrganizationName: z.string().nullable(),
  })
  .meta({ id: 'AccessPerson' });
export type AccessPerson = z.infer<typeof accessPersonSchema>;

// ---------------------------------------------------------------------------------------------------
// TILGANG-SKUFFEN (Atle 4. november, ADVANCE-TILGANG.md §3–4).
//
// Gruppene står i fast rekkefølge: egen organisasjon, artisten med reisefølget, management, motparten,
// eksterne. Begge parter ser hele lista med navn (§4.5, «åpenhet») — det erstatter 253.25-varianten der
// byrået bare så festivalens antall. Hver rad sier selv om den innloggede kan styre den, og hvorfor ikke.
// ---------------------------------------------------------------------------------------------------

export const ACCESS_GROUPS = ['own', 'artist', 'management', 'counterpart', 'external'] as const;
export type AccessGroupKind = (typeof ACCESS_GROUPS)[number];

/** Rollen organisasjonen har på acten — ikke organisasjonstypen (modulene er den eneste regelen). */
export const ACCESS_PARTY_ROLES = ['agency', 'organiser', 'artist', 'management'] as const;
export type AccessPartyRole = (typeof ACCESS_PARTY_ROLES)[number];

/** `active`: har logget inn. `invited`: invitert, ikke logget inn ennå. `link`: personlig lenke. */
export const ACCESS_STATUSES = ['active', 'invited', 'link'] as const;
export type AccessStatus = (typeof ACCESS_STATUSES)[number];

/**
 * Hvorfor raden er låst for den innloggede. `managed_by`: en annen organisasjon styrer den.
 * `org_role`: tilgangen kommer fra rollen i organisasjonen og endres i Innstillinger › Medlemmer.
 */
export const ACCESS_LOCKS = ['managed_by', 'org_role'] as const;

export const accessEntrySchema = z
  .object({
    /** Delingens, reisefølgeradens eller medlemskapets id. Stabil nøkkel for lista. */
    key: z.uuid(),
    personId: z.uuid(),
    displayName: z.string(),
    /** Den innloggede selv: «(deg)». */
    isMe: z.boolean(),
    status: z.enum(ACCESS_STATUSES),
    /** Når invitasjonen gikk ut, for `invited`. */
    invitedAt: wireInstant.nullable(),
    /** Rollen i organisasjonen, for de som har tilgang via den. */
    orgRole: z.enum(ACTOR_ROLES).nullable(),
    /** Tittelen på acten: «Tour manager», «Manager», «Lydtekniker». */
    title: z.string().nullable(),
    permission: z.enum(['view', 'edit']),
    /** Seksjonene delingen dekker (`technical`, `hospitality`, `promo`); null er hele advancen. */
    scopes: z.array(z.string()).nullable(),
    expiresAt: wireInstant.nullable(),
    /** Hvem som delte: «Delt av Atle Andersen». */
    sharedByName: z.string().nullable(),
    readsChat: z.boolean(),
    /** Delingen bak raden, når det er en; det er den endringene går mot. */
    shareId: z.uuid().nullable(),
    /** Reisefølgeraden, for «Fjern innlogging» og «Gi tilgang». */
    partyRowId: z.uuid().nullable(),
    /** Artisttilgangen bak en innlogget i reisefølget eller management (`/booking-artists/:id/access`). */
    artistAccessGrantId: z.uuid().nullable(),
    /** Den innloggede kan endre nivå, omfang, utløp og fjerne tilgangen. */
    canManage: z.boolean(),
    /**
     * Lagt til av pakke A: hva delingen bak raden gjelder. `edition` er delt for hele årgangen og
     * styres derfra, ikke fra programposten. Null for rader uten deling.
     */
    sharedVia: z.enum(['performance', 'edition']).nullable().optional(),
    lock: z
      .object({ reason: z.enum(ACCESS_LOCKS), organizationName: z.string() })
      .nullable(),
  })
  .meta({ id: 'AccessEntry' });
export type AccessEntry = z.infer<typeof accessEntrySchema>;

export const accessGroupSchema = z
  .object({
    kind: z.enum(ACCESS_GROUPS),
    /** Null for «Eksterne». */
    organizationId: z.uuid().nullable(),
    organizationName: z.string().nullable(),
    logoUrl: z.string().nullable(),
    partyRole: z.enum(ACCESS_PARTY_ROLES).nullable(),
    /** Organisasjonen som styrer gruppa: «styres av God Nok Festival». */
    managedByName: z.string().nullable(),
    mine: z.boolean(),
    entries: z.array(accessEntrySchema),
    /** «9 andre i God Nok Festival kan også åpne den via Lineup»: medlemmer uten rolle på acten. */
    others: z
      .object({
        count: z.number().int().nonnegative(),
        via: z.enum(['lineup', 'artists', 'concerts']),
        /** Navnene bak «Vis», når den innloggede får se dem. */
        names: z.array(z.string()).nullable(),
      })
      .nullable(),
    /** Artistgruppa: reisefølget uten innlogging, som står i Personer og ikke her (§4.3). */
    withoutLogin: z.array(
      z.object({ personId: z.uuid(), displayName: z.string(), partyRowId: z.uuid() }),
    ),
    /** Den innloggede kan legge til folk i gruppa (byrået for reisefølget). */
    canGrant: z.boolean(),
    /**
     * Management delt som hele organisasjonen: taket byrået har satt. Medlemmene under styres av
     * management-organisasjonen innenfor det (27.–28. okt.).
     */
    organizationShare: z
      .object({
        shareId: z.uuid().nullable(),
        permission: z.enum(['view', 'edit']),
        scopes: z.array(z.string()).nullable(),
        economy: z.boolean(),
        canManage: z.boolean(),
        /** Lagt til av pakke A: omfanget på artisten, Hele artisten eller Konsertene man står på. */
        artistScope: z.enum(['whole', 'own_gigs']).nullable().optional(),
      })
      .nullable(),
  })
  .meta({ id: 'AccessGroup' });
export type AccessGroup = z.infer<typeof accessGroupSchema>;

export const performanceAccessSchema = z
  .object({
    performanceId: z.uuid(),
    /** Gammel form, til `AccessOverview` er borte. */
    organizations: z.array(accessOrganizationSchema),
    people: z.array(accessPersonSchema),
    /** Alle som kan åpne advancen: radene pluss «andre» i hver gruppe. Tallet på tilgangsknappen. */
    total: z.number().int().nonnegative(),
    groups: z.array(accessGroupSchema),
    chat: z.object({
      /** Hvem som leser chatten. */
      readers: z.array(z.enum(ACCESS_PARTY_ROLES)),
      partyReads: z.boolean(),
      /** «Åpne for reisefølget»: byrået, eller den med full tilgang (vedtatt tidligere). */
      canOpenForParty: z.boolean(),
    }),
    /** Den innloggede kan dele advancen (`performance:share` eller `shared_advance:share`). */
    canShare: z.boolean(),
    /** Forslaget i delingsskjemaet: 14 dager etter konserten (30. sep.). */
    defaultExpiresAt: wireInstant.nullable(),
    /**
     * Lagt til av pakke A: artisten i byråets register, for «Fjern innlogging» og «Gi tilgang»
     * (`/booking-artists/:id/access`). Bare til byrået; null for alle andre.
     */
    bookingArtistId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'PerformanceAccess' });
export type PerformanceAccess = z.infer<typeof performanceAccessSchema>;

/**
 * KOLONNEN «TILGANG» I LINEUP OG KONSERTER (§6) og tilgangsknappen i headeren: maks tre logoer og
 * antallet. Logoene er egen organisasjon, artisten og motparten; har motparten ingen med tilgang, tas
 * neste i rekkefølgen management → eksterne. `null` på en listerad betyr «Advance ikke startet».
 */
export const accessSummarySchema = z
  .object({
    total: z.number().int().nonnegative(),
    logos: z
      .array(
        z.object({
          key: z.string(),
          name: z.string(),
          logoUrl: z.string().nullable(),
          kind: z.enum(ACCESS_GROUPS),
        }),
      )
      .max(3),
    /** Hoverkortet: én linje per organisasjon med antallet. */
    organizations: z.array(
      z.object({
        key: z.string(),
        name: z.string(),
        kind: z.enum(ACCESS_GROUPS),
        partyRole: z.enum(ACCESS_PARTY_ROLES).nullable(),
        count: z.number().int().nonnegative(),
      }),
    ),
  })
  .meta({ id: 'AccessSummary' });
export type AccessSummary = z.infer<typeof accessSummarySchema>;

/** `GET /v1/access-summaries?performanceIds=a,b,…` — kolonnen i listene, én forespørsel per side. */
export const MAX_ACCESS_SUMMARIES = 100;
export const accessSummariesQuerySchema = z.object({
  performanceIds: z
    .string()
    .transform((value) => value.split(',').filter((one) => one !== ''))
    .pipe(z.array(z.uuid()).min(1).max(MAX_ACCESS_SUMMARIES)),
  })
  .meta({ id: 'AccessSummariesQuery' });
export const accessSummariesResponseSchema = z
  .object({
    /** Per programpost; `null` når advancen ikke er startet. Ids uten lesetilgang er utelatt. */
    items: z.record(z.string(), accessSummarySchema.nullable()),
  })
  .meta({ id: 'AccessSummaries' });
export type AccessSummaries = z.infer<typeof accessSummariesResponseSchema>;

const GROUP_ORDER: readonly AccessGroupKind[] = ACCESS_GROUPS;

function groupCount(group: AccessGroup): number {
  return group.entries.length + (group.others?.count ?? 0);
}

/**
 * SAMMENDRAGET AV EN TILGANGSLISTE — én funksjon, så headeren, hoverkortet og listekolonnen aldri er
 * uenige med skuffen. Tallet er alle som kan åpne advancen. Logoene: egen organisasjon, artisten,
 * motparten; har motparten ingen med tilgang, tas neste i rekkefølgen management → eksterne (§3).
 */
export function summarizeAccess(access: Pick<PerformanceAccess, 'groups'>): AccessSummary {
  const counted = access.groups
    .map((group) => ({ group, count: groupCount(group) }))
    .filter((one) => one.count > 0)
    .sort((a, b) => GROUP_ORDER.indexOf(a.group.kind) - GROUP_ORDER.indexOf(b.group.kind));
  const key = (group: AccessGroup): string => group.organizationId ?? group.kind;
  const name = (group: AccessGroup): string => group.organizationName ?? group.kind;
  const pick = (kind: AccessGroupKind) => counted.find((one) => one.group.kind === kind);
  const logos = [
    pick('own'),
    pick('artist'),
    pick('counterpart') ?? pick('management') ?? pick('external'),
  ]
    .filter((one): one is { group: AccessGroup; count: number } => one !== undefined)
    .map(({ group }) => ({
      key: key(group),
      name: name(group),
      logoUrl: group.logoUrl,
      kind: group.kind,
    }));
  return {
    total: counted.reduce((sum, one) => sum + one.count, 0),
    logos,
    organizations: counted.map(({ group, count }) => ({
      key: key(group),
      name: name(group),
      kind: group.kind,
      partyRole: group.partyRole,
      count,
    })),
  };
}
